<template>
  <form class="my-3">

    <b-row v-if="invoiceToEditId.length > 0" class="text-uppercase mb-3">
      <b-col cols="6" class="text-left">
        <h5 class="text-dark font-weight-bold">
          {{$t($getConst.pages.invoice_details.invoice_id)}}:
          <b-skeleton width="5rem" height="20px !important" class="mb-2 d-inline-flex" v-if="is_loading"></b-skeleton>
          <span v-else class="font-weight-light">
            {{invoice.vms_id}}
          </span>
        </h5>
      </b-col>
    </b-row>

    <b-list-group class="text-capitalize mb-4">
      <!--   Invoice inputs   -->
      <b-list-group-item
        class="list-group-section-title d-flex justify-content-between bg-light align-items-center bg-transparent">
        <div class="flex-grow-1">
          <h5 class="my-0 text-uppercase text-secondary font-weight-bold">
            {{$t($getConst.pages.invoice_details.invoice_info_section_title)}}</h5>
        </div>
        <AppSvgIcon height="2rem" :icon="$getConst.svgIcons.invoice_info"/>
      </b-list-group-item>

      <b-list-group-item
        class="d-inline justify-content-between align-items-center font-size-lg pt-3 pb-0 bg-transparent">
        <b-row>
          <b-col cols="6">
            <b-form-group id="invoice_id" :label="$t($getConst.pages.invoice.invoice_id)" label-for="invoice_id-input">
              <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
              <b-form-input v-else
                            id="invoice_id-input"
                            v-model.trim="$v.invoice.invoice_id.$model"
                            :class="{ 'is-invalid': $v.invoice.invoice_id.$error || invoiceExist }"
                            :placeholder="$t($getConst.pages.invoice.enter_invoice_id)"
                            size="lg"
                            required
              ></b-form-input>
              <AppFormFeedback :errors="$v.invoice.invoice_id"/>
              <b-form-invalid-feedback v-if="invoiceExist">
                {{$t($getConst.pages.form.exist_field_error)}}.
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group id="billing_term_category" :label="$t($getConst.pages.invoice.billing_term_category)"
                          label-for="billing_term_category-input">
              <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
              <b-form-input v-else
                            id="billing_term_category-input"
                            v-model.trim="$v.invoice.billing_term_category.$model"
                            :class="{ 'is-invalid': $v.invoice.billing_term_category.$error }"
                            :placeholder="$t($getConst.pages.invoice.enter_billing_term_category)"
                            size="lg"
                            required
              ></b-form-input>
              <AppFormFeedback :errors="$v.invoice.billing_term_category"/>
            </b-form-group>
          </b-col>
        </b-row>

      </b-list-group-item>

      <b-list-group-item
        class="d-inline justify-content-between align-items-center font-size-lg pt-3 pb-0 bg-transparent">
        <b-row>
          <b-col cols="6">
            <b-form-group id="billing_period" :label="$t($getConst.pages.invoice.billing_period)"
                          label-for="billing_period-input">
              <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
              <b-form-input v-else
                            id="billing_period-input"
                            v-model.trim="$v.invoice.billing_period.$model"
                            :class="{ 'is-invalid': $v.invoice.billing_period.$error }"
                            :placeholder="$t($getConst.pages.invoice.enter_billing_period)"
                            size="lg"
                            required
              ></b-form-input>
              <AppFormFeedback :errors="$v.invoice.billing_period"/>
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group id="currency" :label="$t($getConst.pages.invoice.currency)"
                          label-for="currency-input">
              <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
              <b-form-input v-else
                            id="currency-input"
                            v-model.trim="$v.invoice.currency.$model"
                            :class="{ 'is-invalid': $v.invoice.currency.$error }"
                            :placeholder="$t($getConst.pages.invoice.enter_currency)"
                            size="lg"
                            required
              ></b-form-input>
              <AppFormFeedback :errors="$v.invoice.currency"/>
            </b-form-group>
          </b-col>
        </b-row>
      </b-list-group-item>

      <b-list-group-item
        class="d-inline justify-content-between align-items-center font-size-lg pt-3 pb-0 bg-transparent">
        <b-row class="pb-4">
          <b-col cols="6">
            <b-form-group id="payment_status" :label="$t($getConst.pages.invoice.payment_status)"
                          label-for="payment_status-input">
              <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
              <multiselect v-else v-model.trim="$v.invoice.payment_status.$model" :options="['Processed', 'Awaiting']"
                           :multiple="false" :searchable="false" :close-on-select="true"
                           :placeholder="$t($getConst.pages.form.select_option)"
                           :class="{ 'is-invalid': $v.invoice.payment_status.$error }" openDirection="bottom"
              >
                <span slot="noResult">{{$t($getConst.pages.alerts.no_results_found)}}</span>
              </multiselect>
              <AppFormFeedback :errors="$v.invoice.payment_status"/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group id="statement_date" :label="$t($getConst.pages.invoice.statement_date)"
                          label-for="statement_date-input">
              <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
              <b-form-datepicker v-else
                                 id="statement_date-input"
                                 v-model.trim="$v.invoice.statement_date.$model"
                                 :class="{ 'is-invalid': $v.invoice.statement_date.$error }"
                                 :placeholder="$t(this.$getConst.pages.invoice.enter_statement_date)"
                                 :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                 size="lg"
                                 required
                                 :locale="this.$store.getters['auth/scopeDefault'].format_date === 'DD/MM/YYYY'?'fr':'en'"
              ></b-form-datepicker>
              <AppFormFeedback :errors="$v.invoice.statement_date"/>
            </b-form-group>
          </b-col>
        </b-row>
      </b-list-group-item>

      <!--   Assignments inputs   -->
      <b-list-group-item
        class="list-group-section-title d-flex justify-content-between bg-light align-items-center bg-transparent">
        <div class="flex-grow-1">
          <h5 class="my-0 text-uppercase text-secondary font-weight-bold">
            {{$t($getConst.pages.invoice_details.invoice_assignment_section_title)}}</h5>
        </div>
        <AppSvgIcon height="2rem" :icon="$getConst.svgIcons.job"/>
      </b-list-group-item>

      <b-list-group-item
        class="d-inline justify-content-between align-items-center font-size-lg pt-3 pb-0 bg-transparent">
        <b-row class="pb-4">
          <b-col cols="6">
            <b-form-group id="assignment_start_date" :label="$t($getConst.pages.invoice.assignment_start_date)"
                          label-for="assignment_start_date-input">
              <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
              <b-form-datepicker v-else
                                 id="assignment_start_date-input"
                                 v-model.trim="$v.invoice.assignment_start_date.$model"
                                 :class="{ 'is-invalid': $v.invoice.assignment_start_date.$error }"
                                 :placeholder="$t(this.$getConst.pages.invoice.enter_assignment_start_date)"
                                 :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                 size="lg"
                                 required
                                 :max="invoice.assignment_end_date"
                                 :locale="this.$store.getters['auth/scopeDefault'].format_date === 'DD/MM/YYYY'?'fr':'en'"
              ></b-form-datepicker>
              <AppFormFeedback :errors="$v.invoice.assignment_start_date"/>
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group id="assignment_end_date" :label="$t($getConst.pages.invoice.assignment_end_date)"
                          label-for="assignment_end_date-input">
              <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
              <b-form-datepicker v-else
                                 id="assignment_end_date-input"
                                 v-model.trim="$v.invoice.assignment_end_date.$model"
                                 :class="{ 'is-invalid': $v.invoice.assignment_end_date.$error }"
                                 :placeholder="$t(this.$getConst.pages.invoice.enter_assignment_end_date)"
                                 :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                 size="lg"
                                 required
                                 :min="invoice.assignment_start_date"
                                 :locale="this.$store.getters['auth/scopeDefault'].format_date === 'DD/MM/YYYY'?'fr':'en'"
              ></b-form-datepicker>
              <AppFormFeedback :errors="$v.invoice.assignment_end_date"/>
            </b-form-group>
          </b-col>
        </b-row>
      </b-list-group-item>

      <!--   Client inputs   -->
      <b-list-group-item
        class="list-group-section-title d-flex justify-content-between bg-light align-items-center bg-transparent">
        <div class="flex-grow-1">
          <h5 class="my-0 text-uppercase text-secondary font-weight-bold">
            {{$t($getConst.pages.client.timesheet_client_section_title)}}</h5>
        </div>
        <AppSvgIcon height="2rem" :icon="this.$getConst.svgIcons.client"/>
      </b-list-group-item>

      <b-list-group-item
        class="d-inline justify-content-between align-items-center font-size-lg pt-3 pb-0 bg-transparent">
        <b-row class="pb-2">
          <b-col cols="6">
            <b-form-group
              id="client"
              :label="$t($getConst.pages.client.enter_client_name)"
              label-for="timesheet-client-id"
            >
              <multiselect
                v-model="invoice.client_id"
                id="timesheet-client-id"
                track-by="id"
                label="name"
                :placeholder="$t(this.$getConst.pages.form.select_option)"
                :options="clients"
              >
                <span slot="noResult">{{$t($getConst.pages.alerts.no_results_found)}}</span>
              </multiselect>
              <AppFormFeedback :errors="$v.invoice.client_id"/>
            </b-form-group>
          </b-col>
        </b-row>
      </b-list-group-item>

      <!--   Timesheets inputs   -->
      <b-list-group-item
        class="list-group-section-title d-flex justify-content-between bg-light align-items-center bg-transparent">
        <div class="flex-grow-1">
          <h5 class="my-0 text-uppercase text-secondary font-weight-bold">
            {{$t($getConst.pages.invoice_details.invoice_payment_section_title)}}</h5>
        </div>
        <AppSvgIcon height="2rem" :icon="$getConst.svgIcons.invoices"/>
      </b-list-group-item>
      <template v-for="(invoice_payment, index) in $v.invoice.invoice_payments.$each.$iter">
        <AppSection :id="'timesheet-'+index"
                    :title="'timesheet-'+increment(index)"
                    type="sub-section-2"
                    class="pl-4 pr-4 mt-4"
                    :button="invoice.invoice_payments.length > 1"
                    :button-icon="$getConst.icons.delete"
                    button-variant="outline-danger"
                    button-size="sm"
                    @onClick="confirmRemoveInvoicePayment(index)"
        >
          <b-row class="pb-2">
            <b-col cols="6">
              <b-form-group id="timesheet_id"
                            :label="$t($getConst.pages.invoice.timesheet_id)">
                <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
                <multiselect v-else v-model.trim="invoice_payment.timesheet_id.$model"
                             :class="{ 'is-invalid': invoice_payment.timesheet_id.$error }"
                             :placeholder="$t($getConst.pages.form.select_option)" :options="timesheetIds"
                             :multiple="false" openDirection="bottom"
                             :searchable="true" :close-on-select="true"
                             :options-limit="300" :limit="3" :limit-text="limitText"
                             :max-height="600" :show-no-results="false"
                             @search-change="debounceTimesheets"

                >
                  <span slot="noResult">{{$t($getConst.pages.alerts.no_results_found)}}</span>
                </multiselect>
                <AppFormFeedback :errors="invoice_payment.timesheet_id"/>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group id="project_id" :label="$t($getConst.pages.invoice.project_id)"
                            :label-for="'project_id-input'+index">
                <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
                <b-form-input v-else
                              :id="'project_id-input'+index"
                              v-model.trim="invoice_payment.project_id.$model"
                              :class="{ 'is-invalid': invoice_payment.project_id.$error }"
                              :placeholder="$t($getConst.pages.invoice.enter_project_id)"
                              size="lg"
                              required
                ></b-form-input>
                <AppFormFeedback :errors="invoice_payment.project_id"/>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="6">
              <b-form-group id="pay_code" :label="$t($getConst.pages.invoice.pay_code)"
                            :label-for="'pay_code-input'+index">
                <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
                <b-form-input v-else
                              :id="'pay_code-input'+index"
                              v-model.trim="invoice_payment.pay_code.$model"
                              :class="{ 'is-invalid': invoice_payment.pay_code.$error }"
                              :placeholder="$t($getConst.pages.invoice.enter_pay_code)"
                              size="lg"
                              required
                ></b-form-input>
                <AppFormFeedback :errors="invoice_payment.pay_code"/>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group id="total_tax_amount"
                            :label="$t($getConst.pages.invoice.total_tax_amount)"
                            :label-for="'total_tax_amount-input'+index">
                <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
                <b-form-input v-else
                              :id="'total_tax_amount-input'+index"
                              v-model.trim="invoice_payment.total_tax_amount.$model"
                              :class="{ 'is-invalid': invoice_payment.total_tax_amount.$error }"
                              :placeholder="$t($getConst.pages.invoice.enter_total_tax_amount)"
                              size="lg"
                              required
                ></b-form-input>
                <AppFormFeedback :errors="invoice_payment.total_tax_amount"/>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="6">
              <b-form-group id="net_supplier_payable" :label="$t($getConst.pages.invoice.net_supplier_payable)"
                            :label-for="'net_supplier_payable-input'+index">
                <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
                <b-form-input v-else
                              :id="'net_supplier_payable-input'+index"
                              v-model.trim="invoice_payment.net_supplier_payable.$model"
                              :class="{ 'is-invalid': invoice_payment.net_supplier_payable.$error }"
                              :placeholder="$t($getConst.pages.invoice.enter_net_supplier_payable)"
                              size="lg"
                              required
                ></b-form-input>
                <AppFormFeedback :errors="invoice_payment.net_supplier_payable"/>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <b-form-group id="units"
                            :label="$t($getConst.pages.invoice.units)"
                            :label-for="'units-input'+index">
                <b-skeleton type="input" class="mt-0 mb-3 h-75" v-if="is_loading"></b-skeleton>
                <b-form-input v-else
                              :id="'units-input'+index"
                              v-model.trim="invoice_payment.units.$model"
                              :class="{ 'is-invalid': invoice_payment.units.$error }"
                              :placeholder="$t($getConst.pages.invoice.units)"
                              size="lg"
                              required
                ></b-form-input>
                <AppFormFeedback :errors="invoice_payment.units"/>
              </b-form-group>
            </b-col>
          </b-row>

          <div class="text-center mb-2 mt-2">
            <b-button v-if="index == invoice.invoice_payments.length-1" variant="outline" size="lg" class="mr-1"
                      @click="addInvoicePayment">
              {{$getConst.pages.button.add | capitalize}} <i :class="$getConst.icons.add" class="fs-20"></i>
            </b-button>

          </div>
        </AppSection>
      </template>

    </b-list-group>

    <AppConfirmButtons
      :submit-text="$getConst.pages.button.save"
      :cancel-text="$getConst.pages.button.cancel_changes"
      :isLoading="save_is_loading"
      @onSubmit="submit"
      @onCancel="resetData"
    />

    <AppConfirmationModal
      id="confirmation-modal"
      :title=$t($getConst.pages.profile.are_you_sure) | capitalize
      ref="confirmDelete"
      @onCancel="cancelRemoveInvoicePayment"
      @onConfirm="removeInvoicePayment"/>

  </form>

</template>

<script>
  import {required, decimal} from "vuelidate/lib/validators";
  import AppFormFeedback from "../../components/common/form/AppFormFeedback";
  import AppConfirmButtons from "../../components/common/utils/AppConfirmButtons";
  import {addInvoice, deleteInvoicePayment, editInvoice, getInvoice} from "../../api/invoices.api";
  import {debounce} from "lodash";
  import {searchTimesheetsById} from "../../api/vendorTimesheets.api";
  import AppSection from "../../components/common/sections/AppSection";
  import AppConfirmationModal from "../../components/common/modals/AppConfirmationModal";
  import {getEnabledClientByScope} from "../../api/clients.api";

  const initInvoice = () => {
    return {
      invoice_payments: [
        {
          timesheet_id: '',
          project_id: '',
          pay_code: '',
          total_tax_amount: '',
          net_supplier_payable: '',
          units: '',
        }
      ],
      invoice_id: '',
      billing_term_category: '',
      billing_period: '',
      currency: '',
      payment_status: '',
      statement_date: '',
      assignment_start_date: '',
      assignment_end_date: '',
      client_id: '',
    }
  };

  export default {
    name: "InvoiceForm",
    components: {AppSection, AppConfirmButtons, AppFormFeedback, AppConfirmationModal},
    props: {
      invoiceToEditId: {
        default: ''
      }
    },
    data() {
      return {
        invoice: initInvoice(),
        invoiceExist: false,
        is_loading: false,
        save_is_loading: false,
        timesheetIds: [],
        searchIsLoading: false,
        invoicePaymentToDelete: null,
        invoicePaymentIndexToDelete: '',
        clients:[],
      }
    },

    validations: {
      invoice: {
        invoice_payments: {
          required,
          $each: {
            timesheet_id: {required},
            project_id: {required},
            pay_code: {required},
            total_tax_amount: {required, decimal},
            net_supplier_payable: {required, decimal},
            units: {required, decimal},
          }
        },
        invoice_id: {required},
        billing_term_category: {required},
        billing_period: {required},
        currency: {required},
        payment_status: {required},
        statement_date: {required},
        assignment_start_date: {required},
        assignment_end_date: {required},
        client_id: {required},
      }
    },

    methods: {
      isFormInvalid() {
        this.$v.$touch();
        return this.$v.$invalid;

      },
      errorFromServerTreatment(error) {
        if (error.response.data.code === 422) {
          this.invoiceExist = 'invoice_id' in error.response.data.error;
        } else {
          this.handleApiError(error);
        }
      },
      submit() {
        if (this.isFormInvalid()) {
          return;
        }
        this.save_is_loading = true;
        if (this.invoiceToEditId.length === 0) {
          addInvoice(this.invoice).then(
            response => {
              this.$snotify.success(this.$t(this.$getConst.pages.invoice.invoice_added));
              this.save_is_loading = false;
              this.$router.push({name: this.$getConst.routesNames.invoices});
            },
            error => {
              this.errorFromServerTreatment(error);
              this.save_is_loading = false;
            }
          );
        } else {
          editInvoice(this.invoiceToEditId, this.invoice).then(
            response => {
              this.$snotify.success(this.$t(this.$getConst.pages.invoice.invoice_updated));
              this.save_is_loading = false;
              this.$router.push({name: this.$getConst.routesNames.invoices});
            },
            error => {
              this.errorFromServerTreatment(error);
              this.save_is_loading = false;
            }
          );
        }
      },
      resetData() {
        if (this.invoiceToEditId.length !== 0) {
          this.getInvoice();
        } else {
          this.invoice = initInvoice();
          this.$v.$reset();
        }
      },
      getInvoice() {
        this.is_loading = true;
        getInvoice(this.invoiceToEditId).then(
          response => {
            this.invoice = response.data;
            this.invoice.assignment_start_date = this.convertDate(this.invoice.assignment_start_date);
            this.invoice.assignment_end_date = this.convertDate(this.invoice.assignment_end_date);
            this.invoice.statement_date = this.convertDate(this.invoice.statement_date);
            this.getEnabledClientByScope(this.invoice.client_id.scope_id)
            this.is_loading = false;
          },
          error => {
            if (error.response.status === 404) {
              this.$snotify.error(this.$getConst.pages.invoice.redirect_to_invoices_page, this.$t(this.$getConst.pages.alerts.url_not_found));
            } else {
              this.handleApiError(error);
            }

            setTimeout(() => {
              this.$router.push({name: this.$getConst.routesNames.invoices});
            }, 5000);

            this.is_loading = false;
          }
        );
      },
      convertDate(date) {
        return date.split('/').reverse().join('-');
      },
      getTimesheetIds(query) {
        if (query.length > 0) {
          this.searchIsLoading = true;
          searchTimesheetsById({params: {timesheet_id: query}}).then(
            response => {
              this.searchIsLoading = false;
              this.timesheetIds = response.data;
            },
            error => {
              this.searchIsLoading = false;
              this.handleApiError(error);
            }
          )
        }
      },
      limitText(count) {
        return `and ${count} other timesheets`
      },
      addInvoicePayment() {
        this.invoice.invoice_payments.push({
          timesheet_id: '',
          project_id: '',
          pay_code: '',
          total_tax_amount: '',
          net_supplier_payable: '',
          units: ''
        });
      },
      confirmRemoveInvoicePayment(index) {
        if (this.invoice.invoice_payments[index].id === undefined) {
          this.invoice.invoice_payments.splice(index, 1);
        } else {
          this.invoicePaymentToDelete = this.invoice.invoice_payments[index];
          this.invoicePaymentIndexToDelete = index;
          this.$bvModal.show('confirmation-modal');
        }
      },
      removeInvoicePayment() {
        deleteInvoicePayment(this.invoicePaymentToDelete.id).then(
          () => {
            this.invoice.invoice_payments.splice(this.invoicePaymentIndexToDelete, 1);
            this.$refs.confirmDelete.close();
            this.$snotify.success(this.$t(this.$getConst.pages.invoice.invoice_payment_deleted));
          },
          error => {
            this.$refs.confirmDelete.close();
            this.handleApiError(error);
          }
        );
      },
      cancelRemoveInvoicePayment() {
        this.invoicePaymentToDelete = null;
        this.invoicePaymentIndexToDelete = '';
      },
      increment(index) {
        return parseInt(index) + 1;
      },
      getEnabledClientByScope(scope_id) {
        getEnabledClientByScope(scope_id).then((res) => {
          this.clients = res.data
        })
      },
    },
    created() {
      if (this.invoiceToEditId.length !== 0) {
        this.getInvoice();
      }else {
        this.getEnabledClientByScope(this.$store.getters['auth/scopeDefault'].id)
      }

      this.debounceTimesheets = debounce((query) => this.getTimesheetIds(query), 1000);
    },
  }
</script>

<style scoped>
  .list-group {
    border-radius: 0 !important;
  }

  .list-group-item {
    border-right: none !important;
    border-left: none !important;
  }

  .list-group-section-title {
    border-top: none !important;
  }

  .b-skeleton {
    height: 45px !important;
  }
</style>
